import React from 'react';
import { appSections, faqs, iosDetails } from '../content/siteContent';

const Support: React.FC = () => {
  return (
    <div className="px-4 pb-10 pt-8 sm:px-6 md:pt-12">
      <section>
        <div className="mx-auto w-full max-w-5xl">
          <p className="section-kicker">
            Support
          </p>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Get help with Vizo, music visualizer presets, exports, and subscriptions.
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-300">
            Use this page for app help, premium access issues, export questions, and troubleshooting for preset, VIZ, music visualizer, or audio spectrum visualizer imports.
          </p>
        </div>
      </section>

      <section className="py-8">
        <div className="mx-auto grid w-full max-w-6xl gap-4 lg:grid-cols-[1.15fr_0.85fr]">
          <article className="glass-panel rounded-[28px] p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10">
            <h2 className="text-2xl font-semibold text-white">Common search topics</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              This page also helps users who arrive through search terms like music visualizer, audio spectrum visualizer,
              visualizer templates, spectrum presets, VIZ templates, Avee Player templates, and related iPhone music visualization searches.
            </p>
          </article>
          <article className="glass-panel rounded-[28px] p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10">
            <h2 className="text-2xl font-semibold text-white">Avee Player-style searches</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              Vizo is independent, but it is still relevant for people searching for Avee Player-style templates, spectrum visualizer presets,
              and customizable music visualizer workflows on iPhone.
            </p>
          </article>
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto grid w-full max-w-6xl gap-4 lg:grid-cols-2">
          <article className="glass-panel rounded-[28px] p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10">
            <h2 className="text-2xl font-semibold text-white">Contact</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              If you need direct help, send a message with your device model, iOS version, and a short description of the issue.
            </p>
            <ul className="mt-4 space-y-2 text-sm leading-7 text-slate-300">
              <li>Email: support@vizoapp.com</li>
              <li>Response target: within 24-48 hours</li>
              <li>App: Vizo - Music Visualizer</li>
            </ul>
          </article>

          <article className="glass-panel rounded-[28px] p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10">
            <h2 className="text-2xl font-semibold text-white">Useful Links</h2>
            <ul className="mt-4 space-y-2 text-sm leading-7 text-slate-300">
              <li><a className="transition hover:text-white" href="https://apps.apple.com/tr/app/unvis-music-visualizer/id6749325448" target="_blank" rel="noopener noreferrer">Open the App Store listing</a></li>
              <li><a className="transition hover:text-white" href="/privacy">Read the Privacy Policy</a></li>
              <li><a className="transition hover:text-white" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer">Read the Terms of Use</a></li>
            </ul>
          </article>
        </div>
      </section>

      <section className="py-6">
        <div className="mx-auto w-full max-w-5xl">
          <div className="mb-6">
            <p className="section-kicker">
              FAQ
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">Common questions</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {faqs.map((item) => (
              <article className="glass-panel rounded-[28px] p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10" key={item.question}>
                <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-6">
            <p className="section-kicker">
              What The App Includes
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">Main app sections at a glance</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {appSections.map((section) => (
              <article
                className="glass-panel rounded-[28px] p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10"
                key={section.title}
              >
                <h3 className="text-xl font-semibold text-white">{section.title}</h3>
                <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-300">
                  {section.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-cyanGlow"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto w-full max-w-5xl">
          <article className="glass-panel rounded-[28px] p-6 transition duration-300 hover:border-white/20 hover:bg-white/10">
            <h2 className="text-2xl font-semibold text-white">Before you contact support</h2>
            <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-300">
              <li className="flex gap-3"><span className="mt-2 h-2 w-2 rounded-full bg-cyanGlow"></span><span>Confirm your app version and iOS version</span></li>
              <li className="flex gap-3"><span className="mt-2 h-2 w-2 rounded-full bg-cyanGlow"></span><span>Describe the exact export, preset, or subscription issue</span></li>
              <li className="flex gap-3"><span className="mt-2 h-2 w-2 rounded-full bg-cyanGlow"></span><span>Share the steps needed to reproduce the problem</span></li>
              <li className="flex gap-3"><span className="mt-2 h-2 w-2 rounded-full bg-cyanGlow"></span><span>Include screenshots if a visual glitch or message appears</span></li>
            </ul>
          </article>
        </div>
      </section>

      <section className="py-2">
        <div className="glass-panel mx-auto w-full max-w-5xl rounded-[28px] p-6 transition duration-300 hover:border-white/20 hover:bg-white/10">
          <p className="section-kicker">
            Platform Details
          </p>
          <h2 className="mt-4 text-2xl font-semibold text-white">Clear iPhone and iOS details</h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {iosDetails.map((detail) => (
              <div
                className="rounded-2xl border border-white/10 bg-slate-950/30 p-4 text-sm leading-7 text-slate-300 transition duration-300 hover:border-white/20 hover:bg-white/5"
                key={detail}
              >
                {detail}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
